/* src/pages/Signup.jsx
 * Trial signup — no plan picker, no card. Smallest tier is assigned silently.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collectAttribution } from '../utils/attribution';
import { fireSignupConversion } from '../utils/marketingTracking';
import '../styles/auth.css';

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isInviteFlow = searchParams.get('invite') === 'true';

  // Invite flow: read the context AcceptInvite.jsx stashed so we can lock the
  // email field to the invited address and show workspace/inviter context.
  const [inviteContext, setInviteContext] = useState(null);
  useEffect(() => {
    if (!isInviteFlow) return;
    try {
      const raw = sessionStorage.getItem('pendingInviteContext');
      if (raw) {
        const parsed = JSON.parse(raw);
        setInviteContext(parsed);
        if (parsed.invitedEmail) setEmail(parsed.invitedEmail);
      }
    } catch {
      // malformed sessionStorage — fall back to a plain, unlocked email field
    }
  }, [isInviteFlow]);

  // If user just signed up and there's a pending invite, redirect to accept it
  useEffect(() => {
    if (currentUser && currentUser.emailVerified) {
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        navigate(`/accept-invite?token=${pendingToken}`);
      }
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'gocta.ai - Sign up';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  // Persist first-touch attribution from landing URL (or prior visit) on app domain.
  useEffect(() => {
    collectAttribution();
  }, []);

  // Response Test CTA: ?site=example.com prefill for onboarding (see OnboardingWizard).
  useEffect(() => {
    const site = searchParams.get('site')?.trim();
    if (!site) return;
    try {
      sessionStorage.setItem('signupPrefillSite', site.replace(/^https?:\/\//i, '').split('/')[0]);
    } catch {
      /* ignore */
    }
  }, [searchParams]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await signup(email, password, collectAttribution());

      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      const goNext = () => {
        if (pendingToken) {
          // Redirect to accept invite (email will be auto-verified by backend)
          navigate(`/accept-invite?token=${pendingToken}`);
        } else {
          // Normal signup flow - require email verification
          navigate('/verify-email');
        }
      };

      if (!pendingToken && !isInviteFlow) {
        fireSignupConversion(email, goNext);
      } else {
        goNext();
      }
    } catch (err) {
      // Friendly error messages instead of raw Firebase errors
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please log in instead, or use a different email.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters with a mix of letters and numbers.');
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Email/password signup is not enabled. Please contact support.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignup() {
    setError('');
    setLoading(true);

    try {
      const { userCredential, isNewUser } = await loginWithGoogle(collectAttribution());

      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      const goNext = () => {
        if (pendingToken) {
          navigate(`/accept-invite?token=${pendingToken}`);
          return;
        }

        // Google accounts are pre-verified, but check anyway.
        // New users go straight to the onboarding wizard (no card required upfront).
        if (userCredential.user.emailVerified) {
          navigate('/onboarding');
        } else {
          navigate('/verify-email');
        }
      };

      if (isNewUser && !pendingToken && !isInviteFlow) {
        fireSignupConversion(userCredential.user.email, goNext);
      } else {
        goNext();
      }
    } catch (err) {
      setError(err.message || 'Failed to sign up with Google');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap">
        <a href="https://gocta.ai" className="auth-wordmark">gocta<span className="accent">.ai</span></a>
        <div className="auth-card">
          <h1 className="auth-title">
            {isInviteFlow ? 'Create account to accept invitation' : 'Create Your Account'}
          </h1>
          <p className="auth-subtitle">
            {isInviteFlow
              ? inviteContext?.workspaceName
                ? `Create an account to join ${inviteContext.workspaceName}. Your email will be automatically verified.`
                : 'Create an account to accept the team invitation. Your email will be automatically verified.'
              : 'Start your 14-day free trial. No credit card required.'}
          </p>

          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label className="auth-label">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={isInviteFlow && !!inviteContext?.invitedEmail}
                placeholder="you@example.com"
                className="auth-input"
                style={
                  isInviteFlow && inviteContext?.invitedEmail
                    ? { background: 'var(--tint)', cursor: 'not-allowed' }
                    : undefined
                }
              />
              {isInviteFlow && inviteContext?.invitedEmail && (
                <p className="auth-plan-hint" style={{ textAlign: 'left', marginTop: '0.4rem' }}>
                  This invitation is bound to this email address.
                </p>
              )}
            </div>

            <div className="auth-field">
              <label className="auth-label">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="At least 6 characters"
                className="auth-input"
              />
            </div>

            <div className="auth-field" style={{ marginBottom: '1.5rem' }}>
              <label className="auth-label">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Confirm your password"
                className="auth-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary"
            >
              {loading ? 'Creating account...' : 'Create Account & Start Trial'}
            </button>
          </form>

          <div className="auth-divider">
            <div className="auth-divider-line"></div>
            <span className="auth-divider-text">or</span>
            <div className="auth-divider-line"></div>
          </div>

          {/* Google Sign-In Button - Official Design */}
          <button
            onClick={handleGoogleSignup}
            disabled={loading}
            className="auth-btn-google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <g fill="#000" fillRule="evenodd">
                <path d="M9 3.48c1.69 0 2.83.73 3.48 1.34l2.54-2.48C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.96l2.91 2.26C4.6 5.05 6.62 3.48 9 3.48z" fill="#EA4335"/>
                <path d="M17.64 9.2c0-.74-.06-1.28-.19-1.84H9v3.34h4.96c-.21 1.18-.84 2.18-1.79 2.91l2.73 2.12c1.9-1.75 3-4.33 3-7.53z" fill="#4285F4"/>
                <path d="M3.88 10.78A5.54 5.54 0 0 1 3.58 9c0-.62.11-1.22.29-1.78L.96 4.96A9.008 9.008 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.92-2.26z" fill="#FBBC05"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.73-2.12c-.76.53-1.78.9-3.23.9-2.38 0-4.4-1.57-5.12-3.74L.96 13.04C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
              </g>
            </svg>
            <span>Sign up with Google</span>
          </button>

          <div className="auth-footer">
            <p className="auth-footer-text">
              By signing up, you agree to our Terms of Service and Privacy Policy
            </p>
            <Link to="/login" className="auth-link">
              Already have an account? Log in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
